import { pick, pipe } from 'ramda'
import { RSAA } from 'redux-api-middleware'
import {
  getApiPath,
  makeQueryParams,
  mapObjectKeys,
  webStorage,
} from 'sidecar-utility-library'
import { convertToISOString } from 'sidecar-utilities'

import { types, initialState } from './types'

import { replaceOrCreateObjectInArray } from '../utils/replaceOrCreateObjectInArray'
import { updateObjectValueInArrayIfItExists } from '../utils/updateObjectValueInArrayIfItExists'
import { updateOrCreateObjectValueInArray } from '../utils/updateOrCreateObjectValueInArray'
import { mergeArraysRemovingDuplicates } from '../utils/mergeArraysRemovingDuplicates'

export const initializeExpenseOverview = () => ({
  type: types.INITIAL_REQUEST_SUCCESS,
})

export const fetchUploadableExpenseList = memberUuid => ({
  [RSAA]: {
    endpoint: () => {
      const size = 200 // just needs to be a big enough number to cover all
      const page = 0
      const accountUuid = webStorage.getItem('aud')
      const memberUuids = [memberUuid]
      const sort = 'createdAt:desc' // newest expenses go on top

      return getApiPath(
        'exp',
        `expenses/search${makeQueryParams({
          sort,
          size,
          page,
          accountUuid,
          memberUuids,
        })}`,
      )
    },
    method: 'GET',
    types: [
      types.FETCH_UPLOADABLE_EXPENSES_REQUEST,
      types.FETCH_UPLOADABLE_EXPENSES_SUCCESS,
      types.FETCH_UPLOADABLE_EXPENSES_ERROR,
    ],
  },
})

export const fetchDashboardExpenses = ({ memberUuid }) => ({
  [RSAA]: {
    endpoint: () => {
      const size = 3 // only show 3 expenses
      const page = 0
      const accountUuid = webStorage.getItem('aud')
      const sort = 'paymentDueDate:desc'
      // oldest payment due date goes on top
      const memberUuids = [memberUuid]
      const isClosed = false // only show open expenses

      return getApiPath(
        'exp',
        `expenses/search${makeQueryParams({
          sort,
          size,
          page,
          accountUuid,
          memberUuids,
          isClosed,
        })}`,
      )
    },
    method: 'GET',
    types: [
      types.FETCH_DASHBOARD_EXPENSES_REQUEST,
      types.FETCH_DASHBOARD_EXPENSES_SUCCESS,
      types.FETCH_DASHBOARD_EXPENSES_ERROR,
    ],
  },
})

export const fetchExpenseList = (input = {}, resetPagination) => ({
  [RSAA]: {
    endpoint: ({ Expense }) => {
      const accountUuid = webStorage.getItem('aud')
      const size = Expense.searchResults?.size

      // If there are no filters applied or filters have changed, start at page 0
      // Otherwise, increment the current page
      let page = 0
      if (!resetPagination && Expense?.searchResults?.page !== null) {
        page = Expense?.searchResults?.page + 1
      }
      const filterParams = pipe(
        mapObjectKeys({
          page,
          size,
          accountUuid,
          sort: input.sortBy ? `${input.sortBy}:desc` : 'createdAt:desc',
          amounts: input.priceRange,
          providers: input.provider,
          type: input.expenseType,
          isClosed:
            input.status === undefined ? undefined : input.status === 'closed',
          memberUuids: input.member,
          query: input.query,
        }),
        pick([
          'page',
          'size',
          'sort',
          'accountUuid',
          'amounts',
          'providers',
          'type',
          'isClosed',
          'memberUuids',
          'query',
        ]),
      )(input)

      return getApiPath(
        'exp',
        `expenses/search${makeQueryParams(filterParams)}`,
      )
    },
    method: 'GET',
    types: [
      {
        type: types.FETCH_EXPENSES_REQUEST,
        meta: { resetPagination },
      },
      types.FETCH_EXPENSES_SUCCESS,
      types.FETCH_EXPENSES_ERROR,
    ],
  },
})

export const fetchExpenseDetails = expenseUuid => ({
  [RSAA]: {
    endpoint: getApiPath('exp', `expenses/${expenseUuid}`),
    method: 'GET',
    types: [
      {
        type: types.FETCH_EXPENSE_DETAILS_REQUEST,
        meta: { uuid: expenseUuid },
      },
      types.FETCH_EXPENSE_DETAILS_SUCCESS,
      {
        type: types.FETCH_EXPENSE_DETAILS_ERROR,
        meta: { uuid: expenseUuid },
      },
    ],
  },
})

export const fetchActiveExpense = expenseUuid => ({
  /*We decided at one point to name the response object from this endpoint activeExpense, as that's more descriptive as to what
  we're using it for. However, on the BE, the endpoint is an acronym for Explanation of Benefits, as it was originally developed
  to support the EOB pdf feature. */
  [RSAA]: {
    endpoint: getApiPath('exp', `expenses/${expenseUuid}/eob`),
    method: 'GET',
    types: [
      {
        type: types.FETCH_EXPLANATION_OF_BENEFITS_REQUEST,
        meta: { uuid: expenseUuid },
      },
      types.FETCH_EXPLANATION_OF_BENEFITS_SUCCESS,
      {
        type: types.FETCH_EXPLANATION_OF_BENEFITS_ERROR,
        meta: { uuid: expenseUuid },
      },
    ],
  },
})

export const fetchOutstandingExpenses = ({ memberUuid }) => ({
  [RSAA]: {
    endpoint: getApiPath(
      `exp`,
      `expenses/members/${memberUuid}/expenses/outstanding`,
    ),
    method: 'GET',
    types: [
      types.FETCH_OUTSTANDING_EXPENSES_REQUEST,
      types.FETCH_OUTSTANDING_EXPENSES_SUCCESS,
      types.FETCH_OUTSTANDING_EXPENSES_ERROR,
    ],
  },
})

export const fetchOutstandingExpenseBalance = ({ memberUuid }) => ({
  [RSAA]: {
    endpoint: getApiPath(
      `exp`,
      `expenses/members/${memberUuid}/expenses/outstanding`,
    ),
    method: 'GET',
    types: [
      types.FETCH_OUTSTANDING_EXPENSE_BALANCE_REQUEST,
      {
        type: types.FETCH_OUTSTANDING_EXPENSE_BALANCE_SUCCESS,
        meta: { memberUuid },
      },
      types.FETCH_OUTSTANDING_EXPENSE_BALANCE_ERROR,
    ],
  },
})

export const fetchBenefitCardControls = ({ member }) => ({
  [RSAA]: {
    endpoint: getApiPath(
      `exp`,
      `admin/expenses/controls?memberUuid=${member.uuid}`,
    ),
    method: 'GET',
    types: [
      types.FETCH_BENEFIT_CARD_CONTROLS_REQUEST,
      {
        type: types.FETCH_BENEFIT_CARD_CONTROLS_SUCCESS,
        meta: { member },
      },
      types.FETCH_BENEFIT_CARD_CONTROLS_ERROR,
    ],
  },
})

export const fetchCareServices = expenseUuid => ({
  [RSAA]: {
    endpoint: getApiPath('exp', `expenses/${expenseUuid}/care-services`),
    method: 'GET',
    types: [
      types.FETCH_CARE_SERVICES_REQUEST,
      {
        type: types.FETCH_CARE_SERVICES_SUCCESS,
        meta: { uuid: expenseUuid },
      },
      types.FETCH_CARE_SERVICES_ERROR,
    ],
  },
})

export const fetchEobDocs = expenseUuid => ({
  [RSAA]: {
    endpoint: getApiPath(`exp`, `expenses/${expenseUuid}/eob/download`),
    method: 'GET',
    types: [
      types.FETCH_EOB_DOCS_REQUEST,
      types.FETCH_EOB_DOCS_SUCCESS,
      types.FETCH_EOB_DOCS_ERROR,
    ],
  },
})

/**
 * Submit a recurring expense by reusing past invoices
 *
 * @param {string} expenseUuid - The UUID of the expense to submit as recurring
 * @param {object} options - Optional parameters
 * @param {string} options.visitDate - The date of the visit in MM/DD/YYYY
 *   format
 * @param {object} options.bundleInfo - Optional bundle info for the Price Lock
 * @returns RSAA action for submitting recurring expense
 */
export const submitRecurringExpense = (expenseUuid, options = {}) => {
  const { visitDate, bundleInfo } = options

  // Build body only if visitDate is provided (legacy flow)
  // When using expense-form endpoint, no body is needed
  let body = undefined
  if (visitDate) {
    body = {
      serviceDate: convertToISOString(visitDate),
    }

    // Add bundleInfo to body if it exists
    if (bundleInfo) {
      body.bundleInfo = bundleInfo
    }
  }

  return {
    [RSAA]: {
      endpoint: getApiPath(
        'exp',
        `expenses/${expenseUuid}/submit-recurring-expense`,
      ),
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
      types: [
        types.SUBMIT_RECURRING_EXPENSE_REQUEST,
        {
          type: types.SUBMIT_RECURRING_EXPENSE_SUCCESS,
          meta: { expenseUuid },
        },
        types.SUBMIT_RECURRING_EXPENSE_ERROR,
      ],
    },
  }
}

export const fetchRecurringExpenseData = expenseUuid => ({
  [RSAA]: {
    endpoint: getApiPath('exp', `expenses/${expenseUuid}/eob`),
    method: 'GET',
    types: [
      types.FETCH_RECURRING_EXPENSE_DATA_REQUEST,
      types.FETCH_RECURRING_EXPENSE_DATA_SUCCESS,
      types.FETCH_RECURRING_EXPENSE_DATA_ERROR,
    ],
  },
})

export const clearRecurringExpenseData = () => ({
  type: types.CLEAR_RECURRING_EXPENSE_DATA,
})

export const clearNewExpense = () => ({
  type: types.CLEAR_NEW_EXPENSE,
})

export const setMemberAndFetchUploadableExpenses = member => ({
  [RSAA]: {
    endpoint: () => {
      const size = 200 // just needs to be a big enough number to cover all
      const page = 0
      const accountUuid = webStorage.getItem('aud')
      const memberUuids = [member.uuid]
      const sort = 'createdAt:desc' // newest expenses go on top

      return getApiPath(
        'exp',
        `expenses/search${makeQueryParams({
          sort,
          size,
          page,
          accountUuid,
          memberUuids,
        })}`,
      )
    },
    method: 'GET',
    types: [
      {
        type: types.SET_MEMBER_AND_FETCH_UPLOADABLE_EXPENSES_REQUEST,
        meta: { member },
      },
      types.SET_MEMBER_AND_FETCH_UPLOADABLE_EXPENSES_SUCCESS,
      types.SET_MEMBER_AND_FETCH_UPLOADABLE_EXPENSES_ERROR,
    ],
  },
})

export const setNewActiveExpense = expense => ({
  type: types.SET_NEW_ACTIVE_EXPENSE,
  payload: { expense },
})

export const newReceiptUpload = () => ({
  type: types.NEW_RECEIPT_UPLOAD,
})

export const fetchExpenseSuggestions = query => ({
  [RSAA]: {
    endpoint: getApiPath(
      'exp',
      `expenses/search${makeQueryParams({
        page: 0,
        size: 5,
        query,
        accountUuid: webStorage.getItem('aud'),
      })}`,
    ),
    method: 'GET',
    types: [
      types.FETCH_EXPENSE_SUGGESTIONS_REQUEST,
      types.FETCH_EXPENSE_SUGGESTIONS_SUCCESS,
      types.FETCH_EXPENSE_SUGGESTIONS_ERROR,
    ],
  },
})

// The expense service records UNKNOWN when a create call names no reason.
const MEMBER_MANUAL_CREATION_REASON = 'MEMBER_MANUAL'

export const createExpenseWithoutReceipt = memberUuid => {
  return {
    [RSAA]: {
      endpoint: getApiPath(
        'exp',
        `expenses/members/${memberUuid}/expenses${makeQueryParams({
          creationReason: MEMBER_MANUAL_CREATION_REASON,
        })}`,
      ),
      method: 'POST',
      body: JSON.stringify({}),
      types: [
        types.CREATE_EXPENSE_WITHOUT_RECEIPT_REQUEST,
        types.CREATE_EXPENSE_WITHOUT_RECEIPT_SUCCESS,
        types.CREATE_EXPENSE_WITHOUT_RECEIPT_ERROR,
      ],
    },
  }
}

export const uploadExpense = (file, memberUuid, expenseUuid) => {
  const formData = new FormData()
  formData.append('file', file)
  return {
    [RSAA]: {
      endpoint: getApiPath(
        'exp',
        `expenses/receipts/upload${makeQueryParams({
          memberUuid,
          expenseUuid,
        })}`,
      ),
      method: 'POST',
      headers: {
        'Content-Type': 'IGNORE',
      },
      body: formData,
      types: [
        types.CREATE_EXPENSE_REQUEST,
        types.CREATE_EXPENSE_SUCCESS,
        types.CREATE_EXPENSE_ERROR,
      ],
    },
  }
}

export const resetExpenseSearch = () => ({
  type: types.RESET_EXPENSE_SEARCH,
})

/**
 * G20-3009 PR-2: link one expense to another.
 *
 * Attaches `sourceExpenseUuid` to `targetExpenseUuid` (typically a swipe). The
 * BE encodes both UUIDs in the path and returns no linked-expense summary in
 * the response body; the linked-expense summary comes back separately via the
 * PAYMENT_INFO form fetch (see `selectLinkedExpenseFor`). Callers should
 * therefore re-dispatch `fetchPaymentQuestions(sourceExpenseUuid)` after a
 * successful link so the selector's underlying store slice reflects the new
 * link — the reducer here only clears busy/error state.
 *
 * @param {string} sourceExpenseUuid UUID of the expense being reopened/linked.
 * @param {string} targetExpenseUuid UUID of the candidate to link to.
 * @returns RSAA action for linking two expenses.
 */
export const linkExpenses = (sourceExpenseUuid, targetExpenseUuid) => ({
  [RSAA]: {
    endpoint: getApiPath(
      'exp',
      `expenses/${sourceExpenseUuid}/payment-proof-link-expense/${targetExpenseUuid}`,
    ),
    method: 'PUT',
    types: [
      {
        type: types.LINK_EXPENSES_REQUEST,
        meta: { sourceExpenseUuid, targetExpenseUuid },
      },
      {
        type: types.LINK_EXPENSES_SUCCESS,
        meta: { sourceExpenseUuid, targetExpenseUuid },
      },
      {
        type: types.LINK_EXPENSES_ERROR,
        meta: { sourceExpenseUuid, targetExpenseUuid },
      },
    ],
  },
})

/**
 * G20-3009 PR-2: fetch the BE-computed list of expenses a given source expense
 * can be linked to. Response payload is stored under
 * `state.Expense.linkableCandidates.entity` so consumers can select
 * `candidates`, `busy`, and `error` directly. The `sourceExpenseUuid` meta on
 * REQUEST/SUCCESS lets the reducer stamp the slice with the expense the
 * candidates were fetched for — protecting against a stale response overwriting
 * a fresh fetch for a different expense after the member navigates.
 *
 * @param {string} sourceExpenseUuid UUID of the expense candidates are being
 *   fetched for.
 * @returns RSAA action for fetching linkable candidates.
 */
export const fetchLinkableCandidates = sourceExpenseUuid => ({
  [RSAA]: {
    endpoint: getApiPath(
      'exp',
      `expenses/${sourceExpenseUuid}/payment-proof-link-candidates`,
    ),
    method: 'GET',
    types: [
      {
        type: types.FETCH_LINKABLE_CANDIDATES_REQUEST,
        meta: { sourceExpenseUuid },
      },
      {
        type: types.FETCH_LINKABLE_CANDIDATES_SUCCESS,
        meta: { sourceExpenseUuid },
      },
      {
        type: types.FETCH_LINKABLE_CANDIDATES_ERROR,
        meta: { sourceExpenseUuid },
      },
    ],
  },
})

export const clearWorkingExpense = () => ({
  type: types.CLEAR_WORKING_EXPENSE,
})

/**
 * Resets the `linkableCandidates` slice back to its initial shape. Dispatched
 * when the link-expense modal (or any consumer that warmed candidates on open)
 * closes, so a stale list doesn't leak into the next open — especially
 * important on the initial-submission surface where the source expense uuid
 * changes between claims.
 *
 * @returns Plain action.
 */
export const clearLinkableCandidates = () => ({
  type: types.CLEAR_LINKABLE_CANDIDATES,
})

export const clearActiveExpense = () => ({
  type: types.CLEAR_ACTIVE_EXPENSE,
})

export const clearBenefitCardControls = () => ({
  type: types.CLEAR_BENEFIT_CARD_CONTROLS,
})

export const clearEobDocsErrors = () => ({
  type: types.CLEAR_EOB_DOCS_ERRORS,
})

// Prototype feature introduced in PR 3279.
export const fetchExplainMemberResponsibility = expenseUuid => ({
  [RSAA]: {
    endpoint: getApiPath('exp', `explain/member-responsibility/${expenseUuid}`),
    method: 'GET',
    types: [
      types.FETCH_EXPLAIN_MEMBER_RESPONSIBILITY_REQUEST,
      types.FETCH_EXPLAIN_MEMBER_RESPONSIBILITY_SUCCESS,
      types.FETCH_EXPLAIN_MEMBER_RESPONSIBILITY_ERROR,
    ],
  },
})

// Prototype feature introduced in PR 3279.
export const submitExpenseAgentFeedback = (
  expenseUuid,
  thumbsRating,
  feedbackText,
  agentResponse,
) => ({
  [RSAA]: {
    endpoint: getApiPath('exp', `explain/feedback`),
    method: 'POST',
    body: JSON.stringify({
      expenseUuid,
      thumbsRating,
      feedbackText,
      agentResponse,
    }),
    types: [
      types.CREATE_EXPENSE_AGENT_FEEDBACK_REQUEST,
      types.CREATE_EXPENSE_AGENT_FEEDBACK_SUCCESS,
      types.CREATE_EXPENSE_AGENT_FEEDBACK_ERROR,
    ],
  },
})

const benefitCardControlsRequestHandler = state => ({
  ...state,
  benefitCardControls: {
    ...state.benefitCardControls,
    busy: true,
    error: null,
  },
})

const benefitCardControlsErrorHandler = (state, payload) => ({
  ...state,
  benefitCardControls: {
    ...state.benefitCardControls,
    busy: false,
    error: payload.response.message
      ? payload.response.message
      : 'There has been an error',
  },
})

let EXPENSE_REDUCER_LOOKUP = null
const getExpenseReducerLookup = () => {
  if (!EXPENSE_REDUCER_LOOKUP) {
    EXPENSE_REDUCER_LOOKUP = {
      [types.INITIAL_REQUEST_SUCCESS]: state => ({
        ...state,
        isInitialized: true,
      }),
      [types.RESET_EXPENSE_SEARCH]: state => ({
        ...state,
        searchResults: initialState.searchResults,
      }),
      [types.CLEAR_WORKING_EXPENSE]: state => ({
        ...state,
        workingExpense: initialState.workingExpense,
      }),
      [types.CLEAR_ACTIVE_EXPENSE]: state => ({
        ...state,
        activeExpense: initialState.activeExpense,
      }),
      [types.CLEAR_BENEFIT_CARD_CONTROLS]: state => ({
        ...state,
        benefitCardControls: initialState.benefitCardControls,
      }),
      [types.CLEAR_NEW_EXPENSE]: state => ({
        ...state,
        newExpense: {
          workingMember: null,
          activeExpense: null,
          expenseResults: [],
          uploadingNewReceipt: false,
          busy: false,
          error: null,
        },
      }),
      [types.SET_NEW_EXPENSE_MEMBER]: (state, payload) => ({
        ...state,
        newExpense: { ...state.newExpense, workingMember: payload.member },
      }),
      [types.SET_NEW_ACTIVE_EXPENSE]: (state, payload) => ({
        ...state,
        newExpense: { ...state.newExpense, activeExpense: payload.expense },
      }),
      [types.NEW_RECEIPT_UPLOAD]: state => ({
        ...state,
        newExpense: { ...state.newExpense, uploadingNewReceipt: true },
      }),

      // --------------------------
      [types.FETCH_UPLOADABLE_EXPENSES_REQUEST]: state => ({
        ...state,
        newExpense: { ...state.newExpense, busy: true },
      }),
      [types.FETCH_UPLOADABLE_EXPENSES_SUCCESS]: (state, payload) => {
        const { content = [] } = payload
        return {
          ...state,
          newExpense: {
            ...state.newExpense,
            expenseResults: content,
            busy: false,
          },
        }
      },
      [types.FETCH_UPLOADABLE_EXPENSES_ERROR]: state => ({
        ...state,
        newExpense: { ...state.newExpense, busy: false },
      }),

      // --------------------------
      // Combined action that sets member and fetches uploadable expenses atomically
      [types.SET_MEMBER_AND_FETCH_UPLOADABLE_EXPENSES_REQUEST]: (
        state,
        payload,
        meta,
      ) => ({
        ...state,
        newExpense: {
          ...state.newExpense,
          workingMember: meta.member,
          busy: true,
        },
      }),
      [types.SET_MEMBER_AND_FETCH_UPLOADABLE_EXPENSES_SUCCESS]: (
        state,
        payload,
      ) => {
        const { content = [] } = payload
        return {
          ...state,
          newExpense: {
            ...state.newExpense,
            expenseResults: content,
            busy: false,
          },
        }
      },
      [types.SET_MEMBER_AND_FETCH_UPLOADABLE_EXPENSES_ERROR]: state => ({
        ...state,
        newExpense: { ...state.newExpense, busy: false },
      }),

      // --------------------------
      [types.FETCH_EXPENSES_REQUEST]: (state, payload, meta) => ({
        ...state,
        searchResults: {
          ...state.searchResults,
          busy: true,
          error: null,
          entries: meta.resetPagination ? [] : state.searchResults.entries,
        },
      }),
      [types.FETCH_EXPENSES_SUCCESS]: (state, payload) => {
        const { content = [], page, last } = payload
        const endOfResults = last || content.length === 0
        // Page 0 is a fresh search (resetPagination) — REPLACE the entries so
        // results from a prior/competing search don't accumulate. Only later
        // pages (pagination via infinite scroll) merge onto existing entries.
        // Without this, two page-0 fetches racing (e.g. an unfiltered fetch
        // followed by a member-filtered one) merge into a mixed list.
        const entries =
          page === 0
            ? content
            : mergeArraysRemovingDuplicates({
                arrayOne: state.searchResults.entries,
                arrayTwo: content,
                predicate: (a, b) => a.id === b.id,
              })
        return {
          ...state,
          searchResults: {
            ...state.searchResults,
            page: page,
            endOfResults: endOfResults,
            entries: entries,
            busy: false,
          },
        }
      },
      [types.FETCH_EXPENSES_ERROR]: (state, payload) => ({
        ...state,
        searchResults: {
          ...state.searchResults,
          busy: false,
          error: payload.response.message,
        },
      }),

      // --------------------------
      [types.FETCH_EXPENSE_DETAILS_REQUEST]: (state, payload, meta) => ({
        ...state,
        searchResults: {
          ...state.searchResults,
          busy: true,
          entries: updateObjectValueInArrayIfItExists({
            array: state.searchResults.entries,
            itemKey: 'uuid',
            targetValue: meta.uuid,
            valueName: 'error',
            newValue: null,
          }),
        },
        workingExpense: { ...state.workingExpense, busy: true, error: null },
        explainExpense: { entity: null, busy: false, error: null },
      }),
      [types.FETCH_EXPENSE_DETAILS_SUCCESS]: (state, payload, meta) => ({
        ...state,
        searchResults: {
          ...state.searchResults,
          busy: false,
          entries: replaceOrCreateObjectInArray({
            array: state.searchResults.entries,
            itemKey: 'uuid',
            targetValue: meta.uuid,
            newElement: payload,
          }),
        },
        workingExpense: {
          ...state.workingExpense,
          entity: payload,
          busy: false,
        },
      }),
      [types.FETCH_EXPENSE_DETAILS_ERROR]: (state, payload, meta) => ({
        ...state,
        searchResults: {
          ...state.searchResults,
          busy: false,
          entries: updateOrCreateObjectValueInArray({
            array: state.searchResults.entries,
            itemKey: 'uuid',
            targetValue: meta.uuid,
            valueName: 'error',
            newValue: payload.response.message,
            newObject: {
              ...payload.response,
              error: payload.response.message,
              uuid: meta.uuid,
            },
          }),
        },
        workingExpense: {
          ...state.workingExpense,
          error: payload.response.message,
          busy: false,
        },
      }),

      // --------------------------
      [types.FETCH_EXPLANATION_OF_BENEFITS_REQUEST]: (
        state,
        payload,
        meta,
      ) => ({
        ...state,
        searchResults: {
          ...state.searchResults,
          busy: true,
          entries: updateObjectValueInArrayIfItExists({
            array: state.searchResults.entries,
            itemKey: 'uuid',
            targetValue: meta.uuid,
            valueName: 'error',
            newValue: null,
          }),
        },
        activeExpense: { entity: {}, error: null, busy: true },
      }),
      [types.FETCH_EXPLANATION_OF_BENEFITS_SUCCESS]: (
        state,
        payload,
        meta,
      ) => ({
        ...state,
        searchResults: {
          ...state.searchResults,
          entries: replaceOrCreateObjectInArray({
            array: state.searchResults.entries,
            itemKey: 'uuid',
            targetValue: meta.uuid,
            newElement: payload,
          }),
          busy: false,
        },
        activeExpense: { ...state.activeExpense, entity: payload, busy: false },
      }),
      [types.FETCH_EXPLANATION_OF_BENEFITS_ERROR]: (state, payload, meta) => ({
        ...state,
        searchResults: {
          ...state.searchResults,
          busy: false,
          entries: updateOrCreateObjectValueInArray({
            array: state.searchResults.entries,
            itemKey: 'uuid',
            targetValue: meta.uuid,
            valueName: 'error',
            newValue: payload.response.message,
            newObject: {
              ...payload.response,
              error: payload.response.message,
              uuid: meta.uuid,
            },
          }),
        },
        activeExpense: {
          ...state.activeExpense,
          error: payload.response.message,
          busy: false,
        },
      }),

      // --------------------------
      [types.FETCH_OUTSTANDING_EXPENSES_REQUEST]: state => ({
        ...state,
        outstandingExpenses: {
          ...state.outstandingExpenses,
          busy: true,
          error: null,
        },
      }),
      [types.FETCH_OUTSTANDING_EXPENSES_SUCCESS]: (state, payload) => ({
        ...state,
        outstandingExpenses: {
          ...state.outstandingExpenses,
          busy: false,
          outstandingBalance: payload.outstandingBalance || 0,
          entities: payload.outstandingExpenses || [],
        },
      }),
      [types.FETCH_OUTSTANDING_EXPENSES_ERROR]: (state, payload) => ({
        ...state,
        outstandingExpenses: {
          ...state.outstandingExpenses,
          busy: false,
          error: payload.response.message
            ? payload.response.message
            : 'There has been an error',
        },
      }),

      // --------------------------
      [types.FETCH_OUTSTANDING_EXPENSE_BALANCE_REQUEST]:
        benefitCardControlsRequestHandler,
      [types.FETCH_BENEFIT_CARD_CONTROLS_REQUEST]:
        benefitCardControlsRequestHandler,
      [types.FETCH_OUTSTANDING_EXPENSE_BALANCE_SUCCESS]: (
        state,
        payload,
        meta,
      ) => {
        const index = [...state.benefitCardControls.entities].findIndex(
          obj => obj.memberUuid === meta.memberUuid,
        )
        return {
          ...state,
          benefitCardControls: {
            ...state.benefitCardControls,
            busy: false,
            entries: state.benefitCardControls.entities.map((entity, i) =>
              i === index
                ? { ...entity, outstandingBalance: payload.outstandingBalance }
                : entity,
            ),
          },
        }
      },
      [types.FETCH_BENEFIT_CARD_CONTROLS_SUCCESS]: (state, payload) => ({
        ...state,
        benefitCardControls: {
          ...state.benefitCardControls,
          busy: false,
          entities: [...state.benefitCardControls.entities, payload],
        },
      }),
      [types.FETCH_OUTSTANDING_EXPENSE_BALANCE_ERROR]:
        benefitCardControlsErrorHandler,
      [types.FETCH_BENEFIT_CARD_CONTROLS_ERROR]:
        benefitCardControlsErrorHandler,

      // --------------------------
      [types.FETCH_CARE_SERVICES_REQUEST]: state => ({
        ...state,
        scrubs: { ...state.scrubs, busy: true, error: null },
      }),
      [types.FETCH_CARE_SERVICES_SUCCESS]: (state, payload, meta) => ({
        ...state,
        scrubs: {
          ...state.scrubs,
          entities: payload,
          busy: false,
          expenseUuid: meta.uuid,
        },
      }),
      [types.FETCH_CARE_SERVICES_ERROR]: (state, payload) => ({
        ...state,
        scrubs: {
          ...state.scrubs,
          busy: false,
          error: payload.response.message,
        },
      }),

      // --------------------------
      [types.FETCH_EXPENSE_SUGGESTIONS_REQUEST]: state => ({
        ...state,
        searchBarSuggestions: { ...state.searchBarSuggestions, busy: true },
      }),
      [types.FETCH_EXPENSE_SUGGESTIONS_SUCCESS]: (state, payload) => {
        const { content } = payload
        return {
          ...state,
          searchBarSuggestions: {
            ...state.searchBarSuggestions,
            entries: mergeArraysRemovingDuplicates({
              arrayOne: content,
              arrayTwo: state.searchBarSuggestions.entries,
              predicate: (a, b) => a.uuid === b.uuid,
            }),
            busy: false,
          },
        }
      },
      [types.FETCH_EXPENSE_SUGGESTIONS_ERROR]: (state, payload) => ({
        ...state,
        searchBarSuggestions: {
          ...state.searchBarSuggestions,
          busy: false,
          error: payload?.response?.message,
        },
      }),

      // --------------------------
      [types.CREATE_EXPENSE_REQUEST]: state => ({
        ...state,
        receipts: { ...state.receipts, busy: true },
        newExpense: { ...state.newExpense, busy: true, error: null },
      }),
      [types.CREATE_EXPENSE_SUCCESS]: state => ({
        ...state,
        receipts: { ...state.receipts, busy: false },
        newExpense: { ...state.newExpense, busy: false },
      }),
      [types.CREATE_EXPENSE_ERROR]: (state, payload) => ({
        ...state,
        receipts: {
          ...state.receipts,
          busy: false,
          error: payload.response.message,
        },
        newExpense: {
          ...state.newExpense,
          busy: false,
          error: payload.response.message,
        },
      }),

      // --------------------------
      [types.CREATE_EXPENSE_WITHOUT_RECEIPT_REQUEST]: state => ({
        ...state,
        receipts: { ...state.receipts, busy: true },
        newExpense: { ...state.newExpense, busy: true, error: null },
      }),
      [types.CREATE_EXPENSE_WITHOUT_RECEIPT_SUCCESS]: state => ({
        ...state,
        receipts: { ...state.receipts, busy: false },
        newExpense: { ...state.newExpense, busy: false },
      }),
      [types.CREATE_EXPENSE_WITHOUT_RECEIPT_ERROR]: (state, payload) => ({
        ...state,
        receipts: {
          ...state.receipts,
          busy: false,
          error: payload.response.message,
        },
        newExpense: {
          ...state.newExpense,
          busy: false,
          error: payload.response.message,
        },
      }),

      // --------------------------
      [types.FETCH_DASHBOARD_EXPENSES_REQUEST]: state => ({
        ...state,
        dashboardExpenses: { ...state.dashboardExpenses, busy: true },
      }),
      [types.FETCH_DASHBOARD_EXPENSES_SUCCESS]: (state, payload) => {
        const { content = [] } = payload
        return {
          ...state,
          dashboardExpenses: {
            ...state.dashboardExpenses,
            entities: content,
            busy: false,
          },
        }
      },
      [types.FETCH_DASHBOARD_EXPENSES_ERROR]: (state, payload) => ({
        ...state,
        dashboardExpenses: {
          ...state.dashboardExpenses,
          busy: false,
          error: payload?.response?.message
            ? payload.response.message
            : 'There has been an error',
        },
      }),

      // Eob docs
      [types.FETCH_EOB_DOCS_REQUEST]: state => ({
        ...state,
        eobDocs: { ...state.eobDocs, busy: true, error: null },
      }),
      [types.FETCH_EOB_DOCS_SUCCESS]: (state, payload) => ({
        ...state,
        eobDocs: { ...state.eobDocs, busy: false, entity: payload?.url },
      }),
      [types.FETCH_EOB_DOCS_ERROR]: (state, payload) => ({
        ...state,
        eobDocs: {
          ...state.eobDocs,
          busy: false,
          error: payload?.response?.message
            ? payload.response?.message
            : 'There has been an error',
        },
      }),
      [types.CLEAR_EOB_DOCS_ERRORS]: state => ({
        ...state,
        eobDocs: { ...state.eobDocs, error: null },
      }),

      // --------------------------
      // Prototype feature introduced in PR 3279.
      // Introduced in PR 3279
      // Prototyped reducer types: FETCH_EXPLAIN_MEMBER_RESPONSIBILITY_REQUEST,
      // FETCH_EXPLAIN_MEMBER_RESPONSIBILITY_SUCCESS, FETCH_EXPLAIN_MEMBER_RESPONSIBILITY_ERROR,
      // CREATE_EXPENSE_AGENT_FEEDBACK_REQUEST, CREATE_EXPENSE_AGENT_FEEDBACK_SUCCESS,
      // CREATE_EXPENSE_AGENT_FEEDBACK_ERROR
      [types.FETCH_EXPLAIN_MEMBER_RESPONSIBILITY_REQUEST]: state => ({
        ...state,
        explainExpense: { entity: null, busy: true, error: null },
      }),
      [types.FETCH_EXPLAIN_MEMBER_RESPONSIBILITY_SUCCESS]: (
        state,
        payload,
      ) => ({
        ...state,
        explainExpense: {
          ...state.explainExpense,
          entity: payload?.explanation,
          busy: false,
        },
      }),
      [types.FETCH_EXPLAIN_MEMBER_RESPONSIBILITY_ERROR]: (state, payload) => ({
        ...state,
        explainExpense: {
          ...state.explainExpense,
          busy: false,
          error: payload?.response?.message
            ? payload.response?.message
            : 'There has been an error',
        },
      }),
      [types.CREATE_EXPENSE_AGENT_FEEDBACK_REQUEST]: state => ({
        ...state,
        expenseAgentFeedback: { busy: true, error: null },
      }),
      [types.CREATE_EXPENSE_AGENT_FEEDBACK_SUCCESS]: state => ({
        ...state,
        expenseAgentFeedback: { busy: false },
      }),
      [types.CREATE_EXPENSE_AGENT_FEEDBACK_ERROR]: (state, payload) => ({
        ...state,
        expenseAgentFeedback: {
          ...state.expenseAgentFeedback,
          busy: false,
          error: payload?.response?.message
            ? payload.response?.message
            : 'There has been an error',
        },
      }),

      // Recurring Expense
      [types.SUBMIT_RECURRING_EXPENSE_REQUEST]: state => ({
        ...state,
        recurringExpense: {
          ...state.recurringExpense,
          busy: true,
          error: null,
        },
      }),
      [types.SUBMIT_RECURRING_EXPENSE_SUCCESS]: (state, payload, meta) => ({
        ...state,
        recurringExpense: {
          ...state.recurringExpense,
          busy: false,
          error: null,
        },
        // Update the active expense if it matches the submitted expense
        activeExpense: {
          ...state.activeExpense,
          entity:
            meta.expenseUuid === state.activeExpense.entity?.uuid
              ? { ...state.activeExpense.entity, ...payload }
              : state.activeExpense.entity,
        },
      }),
      [types.SUBMIT_RECURRING_EXPENSE_ERROR]: (state, payload) => ({
        ...state,
        recurringExpense: {
          ...state.recurringExpense,
          busy: false,
          error: payload?.response?.message || 'There has been an error',
        },
      }),

      // Recurring Expense Data (Care Services + Provider Info)
      [types.FETCH_RECURRING_EXPENSE_DATA_REQUEST]: state => ({
        ...state,
        recurringExpenseCareServices: {
          ...state.recurringExpenseCareServices,
          busy: true,
          error: null,
        },
      }),
      [types.FETCH_RECURRING_EXPENSE_DATA_SUCCESS]: (state, payload) => ({
        ...state,
        recurringExpenseCareServices: {
          ...state.recurringExpenseCareServices,
          busy: false,
          // Store the full payload including careServices and provider data
          entity: payload || {},
          error: null,
        },
      }),
      [types.FETCH_RECURRING_EXPENSE_DATA_ERROR]: (state, payload) => ({
        ...state,
        recurringExpenseCareServices: {
          ...state.recurringExpenseCareServices,
          busy: false,
          error: payload?.response?.message || 'There has been an error',
        },
      }),
      [types.CLEAR_RECURRING_EXPENSE_DATA]: state => ({
        ...state,
        recurringExpenseCareServices: {
          ...state.recurringExpenseCareServices,
          entity: null,
          error: null,
        },
      }),

      // --------------------------
      // Swipe Linking (G20-3009 PR-2).
      //
      // Reducer choice (a): the SUCCESS handler no longer merges a
      // `linkedExpense` onto the active-expense entity. The BE encodes both
      // UUIDs in the PUT path and the response body carries no linked-expense
      // summary — that summary comes back via the PAYMENT_INFO form fetch, and
      // `selectLinkedExpenseFor` now reads from
      // `state.Expense.paymentQuestions.entity.formFields` (see PR-1). Callers
      // MUST re-dispatch `fetchPaymentQuestions(sourceExpenseUuid)` on success
      // so the payment-questions slice reflects the new link.
      // `linkExpensesBusy` is scoped to the link action; do NOT co-opt the
      // generic `activeExpense.busy` here. `Detail/index.jsx` renders a full-
      // page Spinner while `activeExpense.busy` is true, which would unmount
      // SubmitProofPrompt → LinkSwipeModal the instant the PUT starts — the
      // modal would then remount fresh (without its open/error state) when
      // the request settled. LinkSwipeModal has its own local `busy` state
      // that disables the confirm button, so this scoped field is sufficient
      // for any consumer that wants to observe an in-flight link.
      [types.LINK_EXPENSES_REQUEST]: state => ({
        ...state,
        activeExpense: {
          ...state.activeExpense,
          linkExpensesBusy: true,
          linkExpensesError: null,
        },
      }),
      [types.LINK_EXPENSES_SUCCESS]: state => ({
        ...state,
        activeExpense: {
          ...state.activeExpense,
          linkExpensesBusy: false,
          linkExpensesError: null,
        },
      }),
      [types.LINK_EXPENSES_ERROR]: (state, payload, meta) => {
        const entity = state.activeExpense?.entity ?? {}
        const entityUuid = entity.uuid ?? entity.expenseUuid
        // Mirror the SUCCESS stale-uuid guard: only surface the error on the
        // active expense that was the source of the failed link. If the user
        // has navigated away, drop the busy flag but leave the currently-
        // viewed expense's error state alone so a stale failure can't poison
        // it.
        const isStale =
          !!meta?.sourceExpenseUuid && entityUuid !== meta.sourceExpenseUuid
        if (isStale) {
          return {
            ...state,
            activeExpense: {
              ...state.activeExpense,
              linkExpensesBusy: false,
            },
          }
        }
        return {
          ...state,
          activeExpense: {
            ...state.activeExpense,
            linkExpensesBusy: false,
            linkExpensesError:
              payload?.response?.message || 'There has been an error',
          },
        }
      },

      // --------------------------
      // Swipe Linking candidates (G20-3009 PR-2). Populate the
      // `linkableCandidates` slice for BE-driven candidate lists. `meta`
      // stamps the source expense the fetch was for so stale responses can be
      // ignored downstream.
      [types.FETCH_LINKABLE_CANDIDATES_REQUEST]: (state, payload, meta) => ({
        ...state,
        linkableCandidates: {
          ...state.linkableCandidates,
          busy: true,
          error: null,
          sourceExpenseUuid: meta?.sourceExpenseUuid ?? null,
        },
      }),
      [types.FETCH_LINKABLE_CANDIDATES_SUCCESS]: (state, payload, meta) => {
        // Stale-response guard: if a new fetch already replaced the source
        // uuid on the slice, drop this payload rather than overwrite it.
        const currentSource = state.linkableCandidates?.sourceExpenseUuid
        const responseSource = meta?.sourceExpenseUuid
        if (
          currentSource &&
          responseSource &&
          currentSource !== responseSource
        ) {
          return state
        }
        // Normalize payload shape — BE may return either `{ candidates: [...] }`
        // or a bare array. Consumers always read `entity.candidates`.
        const candidates = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.candidates)
            ? payload.candidates
            : []
        return {
          ...state,
          linkableCandidates: {
            ...state.linkableCandidates,
            busy: false,
            entity: { candidates },
            error: null,
          },
        }
      },
      [types.FETCH_LINKABLE_CANDIDATES_ERROR]: (state, payload) => ({
        ...state,
        linkableCandidates: {
          ...state.linkableCandidates,
          busy: false,
          error: payload?.response?.message || 'There has been an error',
        },
      }),
      [types.CLEAR_LINKABLE_CANDIDATES]: state => ({
        ...state,
        linkableCandidates: initialState.linkableCandidates,
      }),
    }
  }
  return EXPENSE_REDUCER_LOOKUP
}

export default (state = initialState, { type, payload = {}, meta = {} }) =>
  getExpenseReducerLookup()[type]?.(state, payload, meta) ?? state
